import { create } from 'zustand';
import apiClient from '../api/client';
import type { Book, BookCreatePayload, BookUpdatePayload } from '../books/types';

interface BooksState {
  books: Book[];
  loading: boolean;
  error: string | null;
  fetchBooks: () => Promise<void>;
  addBook: (book: BookCreatePayload) => Promise<Book | undefined>;
  updateBook: (id: string, updates: BookUpdatePayload) => Promise<Book | undefined>;
  deleteBook: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useBooksStore = create<BooksState>((set) => ({
  books: [],
  loading: false,
  error: null,

  fetchBooks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await apiClient.get<{ books: Book[] }>('/api/books');
      set({ books: data.books, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  addBook: async (book) => {
    set({ error: null });
    try {
      const created = await apiClient.post<Book>('/api/books', book);
      set((state) => ({ books: [...state.books, created] }));
      return created;
    } catch (err) {
      set({ error: (err as Error).message });
      return undefined;
    }
  },

  updateBook: async (id, updates) => {
    set({ error: null });
    try {
      const updated = await apiClient.put<Book>(`/api/books/${id}`, updates);
      set((state) => ({
        books: state.books.map((book) => (book.id === id ? updated : book)),
      }));
      return updated;
    } catch (err) {
      set({ error: (err as Error).message });
      return undefined;
    }
  },

  deleteBook: async (id) => {
    set({ error: null });
    try {
      await apiClient.delete(`/api/books/${id}`);
      set((state) => ({ books: state.books.filter((book) => book.id !== id) }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  clearError: () => set({ error: null }),
}));

export type { Book, BookCreatePayload, BookUpdatePayload } from '../books/types';
